import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TicketAttachment } from '@nbh/shared';
import { AttachmentImage } from '../ui/AttachmentImage';
import { PhotoLightbox } from './PhotoLightbox';
import { altKeyFor, attachmentCaption, groupAttachments, isPhoto } from '../../lib/attachments';

export interface TicketPhotoGalleryProps {
  attachments: TicketAttachment[];
}

/**
 * Everything ever attached to this job, grouped by the moment it was taken (R8).
 *
 * Lifecycle stages in lifecycle order — Issue, Completion, Verification — then checklist
 * evidence under its own heading, because a photo of one SOP step is not a claim about
 * the job's before-and-after and must not be filed as one.
 *
 * **Returns null when there is nothing.** The tab that renders this is itself conditional
 * (see TicketDetail), so this is the belt to that braces: a job with no photos has no
 * gallery, no heading and no empty state, because having none is normal (D2, D33).
 */
export function TicketPhotoGallery({ attachments }: TicketPhotoGalleryProps) {
  const { t } = useTranslation();
  const [viewingId, setViewingId] = useState<string | null>(null);

  const groups = groupAttachments(attachments);
  if (groups.length === 0) return null;

  // The viewer pages through the photos in the order they are laid out, so a swipe out
  // of the Issue group lands in Completion rather than in whatever the wire order was.
  const photos = groups.flatMap((group) => group.items).filter(isPhoto);
  const viewingIndex = photos.findIndex((photo) => photo.id === viewingId);

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.labelKey}>
          <h2 className="mb-2 text-sm font-semibold text-slate-300">{t(group.labelKey)}</h2>

          <ul className="grid grid-cols-2 gap-3">
            {group.items.map((item) => (
              <li key={item.id}>
                {isPhoto(item) ? (
                  <GalleryPhoto photo={item} onOpen={() => setViewingId(item.id)} />
                ) : (
                  <VoiceNote attachment={item} />
                )}
                <p className="mt-1 text-xs text-slate-400">{attachmentCaption(item, t)}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {viewingIndex >= 0 && (
        <PhotoLightbox
          photos={photos}
          startIndex={viewingIndex}
          onClose={() => setViewingId(null)}
        />
      )}
    </div>
  );
}

function GalleryPhoto({ photo, onOpen }: { photo: TicketAttachment; onOpen: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('attachment.open')}
      className="block w-full overflow-hidden rounded-lg bg-transparent p-0"
    >
      <AttachmentImage
        src={photo.url}
        alt={t(altKeyFor(photo.stage))}
        width={photo.width}
        height={photo.height}
        className="aspect-square w-full rounded-lg object-cover"
      />
    </button>
  );
}

/**
 * A voice note, played in place.
 *
 * Kept in the gallery rather than filtered out of it: a voice note is media hanging off
 * the same ticket, uploaded by the same person at the same stage, and no other screen in
 * the client renders one — excluding it here would mean a technician's spoken note about
 * a fault could be recorded and then never heard by anyone. It is not an image, so it
 * gets an `<audio>` control instead of a tile, and it is not offered to the lightbox.
 *
 * `preload="none"` because these are full downloads over a basement connection: the bytes
 * are fetched when someone presses play, not when the tab opens.
 */
function VoiceNote({ attachment }: { attachment: TicketAttachment }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg bg-slate-900 p-2">
      <p className="mb-1 text-xs font-medium text-slate-300">{t('attachment.voiceNote')}</p>
      <audio controls preload="none" src={attachment.url} className="w-full" />
    </div>
  );
}
